import React from 'react';
import { AnalyzerWeeklyPrompt } from '@Models/types';
import ArtilleryPromptDropdown from './ArtilleryPromptDropdown';
import { ArtilleryReport } from '@Models/types';
import {
  DownloadSimple,
  CaretDown,
  File,
  Check,
  Warning,
  X,
  Users,
  Lightning,
  Globe,
  ChartBar,
  TrendDown,
  Clock,
  CheckCircle,
  DotOutline,
  Pulse,
  Pencil,
  Circle,
  SquaresFour,
} from '@phosphor-icons/react';
import { useAuth } from '@Context/CognitoAuthProvider';

interface ArtilleryMainPanelProps {
  singleReport: ArtilleryReport | null;
  isDownloading: boolean;
  downloadPDF: (fileName: string) => void;
  isNavDropdownOpen: boolean;
  setIsNavDropdownOpen: (open: boolean) => void;
  currentSection: string;
  setCurrentSection: (section: string) => void;
  scrollToSection: (sectionId: string) => void;
  // Prompt Management (no context)
  prompts?: AnalyzerWeeklyPrompt[];
  promptsLoading?: boolean;
  onCreatePrompt?: (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => void;
  onSetDefaultPrompt?: (promptTitle: string, authorEmail: string) => void;
  onViewPrompt?: (prompt: AnalyzerWeeklyPrompt) => void;
}

const formatDate = (timestamp: string) => {
  const date = new Date(Number(timestamp));
  return date.toLocaleDateString('en-GB');
};

const ArtilleryMainPanel: React.FC<ArtilleryMainPanelProps> = ({
  singleReport,
  isDownloading,
  downloadPDF,
  isNavDropdownOpen,
  setIsNavDropdownOpen,
  currentSection,
  setCurrentSection,
  scrollToSection,
  // unused props removed after self-contained dropdown
}) => {
  const { user } = useAuth();
  const isAdmin = user ? user.admin : false;

  return (
    <div className="h-full w-4/5">
      <div className="flex h-full w-full flex-col gap-6">
        <div
          className={`flex w-full flex-row ${singleReport ? 'justify-between' : 'justify-end'}`}
        >
          {singleReport && (
            <div className="flex w-full flex-col gap-2 px-8">
              <span className="text-2xl font-bold text-gray-700">
                <span className="text-black">
                  {singleReport.reportName.toUpperCase()}
                </span>
              </span>
              <div className="relative flex items-center gap-2">
                <button
                  onClick={() => setIsNavDropdownOpen(!isNavDropdownOpen)}
                  className="hover:bg-primary-800 flex items-center gap-1 rounded bg-primary-700 px-3 py-2 text-sm text-white"
                >
                  {currentSection}
                  <CaretDown size={12} weight="bold" />
                </button>
                <button
                  onClick={() => downloadPDF(singleReport.reportName)}
                  disabled={isDownloading}
                  className="flex flex-row items-center gap-1 rounded bg-green-700 px-3 py-2 text-sm text-white hover:bg-green-800"
                >
                  <DownloadSimple />
                  {isDownloading ? 'Downloading...' : 'Download PDF'}
                </button>
                {isNavDropdownOpen && (
                  <div
                    onMouseLeave={() => setIsNavDropdownOpen(false)}
                    className="absolute left-0 top-0 z-10 mt-10 w-56 rounded border border-gray-400 bg-white p-2 shadow-lg"
                  >
                    <ul className="custom-scroll max-h-60 overflow-auto px-2 py-1 font-inter text-sm">
                      <li
                        onClick={() => {
                          scrollToSection('executive-summary');
                          setCurrentSection('Executive Summary');
                          setIsNavDropdownOpen(false);
                        }}
                        className="cursor-pointer rounded-md px-4 py-2 hover:bg-primary-100"
                      >
                        Executive Summary
                      </li>
                      <li
                        onClick={() => {
                          scrollToSection('core-web-vitals');
                          setCurrentSection('Core Web Vitals');
                          setIsNavDropdownOpen(false);
                        }}
                        className="cursor-pointer rounded-md px-4 py-2 hover:bg-primary-100"
                      >
                        Core Web Vitals
                      </li>
                      <li
                        onClick={() => {
                          scrollToSection('user-experience');
                          setCurrentSection('User Experience');
                          setIsNavDropdownOpen(false);
                        }}
                        className="cursor-pointer rounded-md px-4 py-2 hover:bg-primary-100"
                      >
                        User Experience
                      </li>
                      <li
                        onClick={() => {
                          scrollToSection('page-performance');
                          setCurrentSection('Page Performance');
                          setIsNavDropdownOpen(false);
                        }}
                        className="cursor-pointer rounded-md px-4 py-2 hover:bg-primary-100"
                      >
                        Page Performance
                      </li>
                      <li
                        onClick={() => {
                          scrollToSection('recommendations');
                          setCurrentSection('Recommendations');
                          setIsNavDropdownOpen(false);
                        }}
                        className="cursor-pointer rounded-md px-4 py-2 hover:bg-primary-100"
                      >
                        Recommendations
                      </li>
                      <li
                        onClick={() => {
                          scrollToSection('implementation-notes');
                          setCurrentSection('Implementation Notes');
                          setIsNavDropdownOpen(false);
                        }}
                        className="cursor-pointer rounded-md px-4 py-2 hover:bg-primary-100"
                      >
                        Implementation Notes
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="">{isAdmin && <ArtilleryPromptDropdown />}</div>
        </div>

        {singleReport && (
          <div className="custom-scroll flex h-full w-full flex-col overflow-y-auto px-6 text-black">
            <div
              id="executive-summary"
              className="flex w-full flex-col gap-4 border-b border-gray-300 p-2 text-sm"
            >
              <div className="flex flex-col">
                <span className="flex flex-row items-center gap-2 text-xl font-bold text-primary-700">
                  <File color="#081c4c" /> Executive Summary
                </span>
                <div className="flex flex-row gap-4">
                  <span className="text-sm">
                    Test Date:{' '}
                    {formatDate(singleReport.executiveSummary.testDate)}
                  </span>
                  <span className="text-sm">
                    Environment: {singleReport.executiveSummary.environment}
                  </span>
                </div>

                <div className="mt-4 flex w-full flex-col rounded-md border border-gray-400 bg-gray-200 p-4 text-justify">
                  <span className="text-base font-semibold">
                    Overall Performance
                  </span>
                  <span className="mb-2 text-sm">
                    {singleReport.summary.overallPerformance}
                  </span>
                  <span className="text-base font-semibold">
                    Reliability and Risks
                  </span>
                  <span className="mb-2 text-sm">
                    {singleReport.summary.reliabilityAndRisks}
                  </span>
                  <span className="text-base font-semibold">
                    Impact and Recommendations
                  </span>
                  <span className="text-sm">
                    {singleReport.summary.impactAndRecommendations}
                  </span>
                </div>
              </div>
              <div className="flex flex-row justify-between">
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">Overall Rating</span>
                  <span
                    className={`flex flex-row items-center justify-center gap-1 rounded-lg px-2 py-1 font-semibold ${
                      singleReport.executiveSummary.overallRating === 'Good'
                        ? 'bg-green-200 text-green-800'
                        : singleReport.executiveSummary.overallRating ===
                            'Needs Improvement'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-200 text-red-800'
                    }`}
                  >
                    {singleReport.executiveSummary.overallRating === 'Good' ? (
                      <Check />
                    ) : singleReport.executiveSummary.overallRating ===
                      'Needs Improvement' ? (
                      <Warning />
                    ) : (
                      <X />
                    )}
                    {singleReport.executiveSummary.overallRating}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span>Test Duration</span>
                  <span className="text-2xl">
                    {singleReport.executiveSummary.duration}s
                  </span>
                </div>
              </div>
              <div className="flex flex-row gap-10">
                <div className="flex w-1/2 flex-col gap-1">
                  <span className="flex flex-row items-center gap-1 font-semibold text-gray-700">
                    <span className="text-primary-700">
                      <Users />
                    </span>{' '}
                    Virtual Users
                  </span>
                  <div className="flex flex-col text-gray-500">
                    <span>
                      Created:{' '}
                      {singleReport.executiveSummary.virtualUsers.created}
                    </span>
                    <span>
                      Completed:{' '}
                      {singleReport.executiveSummary.virtualUsers.completed}
                    </span>
                    <span className="text-red-600">
                      Failed:{' '}
                      {singleReport.executiveSummary.virtualUsers.failed}
                    </span>
                  </div>
                </div>
                <div className="flex w-1/2 flex-col text-gray-700">
                  <span className="font-semibold">Key Findings</span>
                  <div>
                    <ul>
                      {singleReport.executiveSummary.keyFindings.map(
                        (finding, index) => (
                          <li key={index}>{finding}</li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="core-web-vitals"
              className="flex w-full flex-col gap-4 border-b p-2 text-sm"
            >
              <div className="flex flex-col">
                <span className="flex flex-row items-center gap-2 text-xl font-bold text-primary-700">
                  <Lightning color="#071D49" /> Core Web Vitals
                </span>
                <span>Performance metrics for each tested page</span>
              </div>
              {singleReport.coreWebVitals.pages.map((page, index) => (
                <div key={index} className="flex flex-col">
                  <span className="flex flex-row items-center gap-1 font-semibold">
                    <Globe /> {page.page}
                  </span>
                  <div className="flex flex-row gap-10">
                    <div className="flex w-1/4 flex-col justify-between">
                      <div className="flex flex-row justify-between">
                        <span className="font-semibold text-gray-700">FCP</span>
                        <span
                          className={`rounded-md px-2 ${
                            page.fcp?.rating === 'Good'
                              ? 'bg-green-200 text-green-700'
                              : page.fcp?.rating === 'Poor'
                                ? 'bg-red-200 text-red-700'
                                : page.fcp?.rating === 'Needs Improvement'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {page.fcp?.rating ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between">
                        <span>Avg: {page.fcp?.average ?? 'N/A'}</span>
                        <span>P95: {page.fcp?.p95 ?? 'N/A'}</span>
                      </div>
                    </div>
                    <div className="flex w-1/4 flex-col justify-between">
                      <div className="flex flex-row justify-between">
                        <span className="font-semibold text-gray-700">LCP</span>
                        <span
                          className={`rounded-md px-2 ${
                            page.lcp?.rating === 'Good'
                              ? 'bg-green-200 text-green-700'
                              : page.lcp?.rating === 'Poor'
                                ? 'bg-red-200 text-red-700'
                                : page.lcp?.rating === 'Needs Improvement'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {page.lcp?.rating ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between">
                        <span>Avg: {page.lcp?.average ?? 'N/A'}</span>
                        <span>P95: {page.lcp?.p95 ?? 'N/A'}</span>
                      </div>
                    </div>
                    <div className="flex w-1/4 flex-col justify-between">
                      <div className="flex flex-row justify-between">
                        <span className="font-semibold text-gray-700">CLS</span>
                        <span
                          className={`rounded-md px-2 ${
                            page.cls?.rating === 'Good'
                              ? 'bg-green-200 text-green-700'
                              : page.cls?.rating === 'Poor'
                                ? 'bg-red-200 text-red-700'
                                : page.cls?.rating === 'Needs Improvement'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {page.cls?.rating ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between">
                        <span>Avg: {page.cls?.average ?? 'N/A'}</span>
                        <span>P95: {page.cls?.p95 ?? 'N/A'}</span>
                      </div>
                    </div>
                    <div className="flex w-1/4 flex-col justify-between">
                      <div className="flex flex-row justify-between">
                        <span className="font-semibold text-gray-700">
                          TTFB
                        </span>
                        <span
                          className={`rounded-md px-2 ${
                            page.ttfb?.rating === 'Good'
                              ? 'bg-green-200 text-green-700'
                              : page.ttfb?.rating === 'Poor'
                                ? 'bg-red-200 text-red-700'
                                : page.ttfb?.rating === 'Needs Improvement'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {page.ttfb?.rating ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between">
                        <span>Avg: {page.ttfb?.average ?? 'N/A'}</span>
                        <span>P95: {page.ttfb?.p95 ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              id="user-experience"
              className="flex flex-row justify-between gap-10 border-b p-2 text-sm"
            >
              <div className="flex w-1/2 flex-col gap-4">
                <span className="flex flex-row items-center gap-2 text-xl font-bold text-primary-700">
                  <ChartBar color="#071D49" /> User Experience
                </span>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <span className="font-semibold text-gray-700">
                        Success Rate
                      </span>
                      <span
                        className={`text-xl font-semibold ${
                          !singleReport.userExperienceMetrics.successRate
                            ? 'text-gray-500'
                            : parseFloat(
                                  singleReport.userExperienceMetrics.successRate
                                ) >= 90
                              ? 'text-green-600'
                              : parseFloat(
                                    singleReport.userExperienceMetrics
                                      .successRate
                                  ) >= 70
                                ? 'text-yellow-500'
                                : 'text-red-600'
                        }`}
                      >
                        {singleReport.userExperienceMetrics.successRate ??
                          'N/A'}
                      </span>
                    </div>

                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-300">
                      <div
                        className={`absolute left-0 top-0 h-2 rounded-full ${
                          parseFloat(
                            singleReport.userExperienceMetrics.successRate
                          ) >= 90
                            ? 'bg-green-600'
                            : parseFloat(
                                  singleReport.userExperienceMetrics.successRate
                                ) >= 70
                              ? 'bg-yellow-500'
                              : 'bg-red-600'
                        }`}
                        style={{
                          width:
                            singleReport.userExperienceMetrics.successRate ??
                            '0%',
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">
                      HTTP Response Codes
                    </span>
                    <div className="flex flex-row gap-4">
                      <div className="flex w-1/2 flex-col">
                        <div className="flex flex-row justify-between">
                          <span className="text-green-600">200:</span>{' '}
                          <span className="text-gray-800">
                            {singleReport.userExperienceMetrics
                              .httpResponseCodes['200'] ?? 'N/A'}
                          </span>
                        </div>
                        <div className="flex flex-row justify-between">
                          <span className="text-green-600">204:</span>{' '}
                          <span className="text-gray-800">
                            {singleReport.userExperienceMetrics
                              .httpResponseCodes['204'] ?? 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="flex w-1/2 flex-col">
                        <div className="flex flex-row justify-between">
                          <span className="text-green-600">202:</span>{' '}
                          <span className="text-gray-800">
                            {singleReport.userExperienceMetrics
                              .httpResponseCodes['202'] ?? 'N/A'}
                          </span>
                        </div>
                        <div className="flex flex-row justify-between">
                          <span className="text-red-600">429:</span>{' '}
                          <span className="text-gray-800">
                            {singleReport.userExperienceMetrics
                              .httpResponseCodes['429'] ?? 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div id="error-analysis" className="flex w-1/2 flex-col gap-4">
                <span className="flex flex-row items-center gap-1 text-xl font-bold text-black">
                  <Warning color="#071D49" /> Error Analysis
                </span>
                <div className="flex flex-col gap-2">
                  {singleReport.errorAnalysis.errors.map((error, index) => (
                    <div
                      key={index}
                      className="flex flex-col justify-between rounded-md border border-red-200 p-3"
                    >
                      <span className="font-semibold">{error.type}</span>
                      <span>Count: {error.count}</span>
                      <span>{error.impact}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              id="page-performance"
              className="flex w-full flex-col gap-4 border-b p-2 text-sm"
            >
              <span className="flex flex-row items-center gap-2 text-xl font-bold text-primary-700">
                <Clock color="#071D49" /> Page Performance
              </span>
              <div className="flex flex-col gap-3">
                <div className="flex flex-row justify-between">
                  <span className="font-semibold text-gray-700">
                    Average Response Time
                  </span>
                  <span className="text-2xl">
                    {singleReport.pagePerformance.averageResponseTime
                      ? `${(singleReport.pagePerformance.averageResponseTime / 1000).toFixed(1)} seconds`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-gray-700">
                    Slowest Pages
                  </span>
                  <div className="flex flex-col gap-5">
                    {singleReport.pagePerformance.slowestPages.map(
                      (page, index) => (
                        <div
                          key={index}
                          className="flex flex-row justify-between"
                        >
                          <span>{page.url}</span>
                          <span className="flex flex-row items-center gap-1 rounded-md border border-red-300 bg-red-200 px-2 py-1 text-red-600">
                            <TrendDown />
                            {(page.responseTime / 1000).toFixed(2)}s
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              id="recommendations"
              className="flex w-full flex-row gap-10 border-b p-2 text-sm"
            >
              <div className="flex w-1/2 flex-col gap-4">
                <span className="flex flex-row items-center gap-2 text-xl font-bold text-primary-700">
                  <CheckCircle color="#071D49" /> Recommendations
                </span>
                <div className="flex flex-col gap-2">
                  {singleReport.recommendations.map((rec, index) => (
                    <div key={index} className="flex flex-row justify-between">
                      <span className="flex flex-row items-center gap-1">
                        <DotOutline /> {rec}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex w-1/2 flex-col gap-4">
                <span className="flex flex-row items-center gap-2 text-xl font-bold text-black">
                  <Pulse color="#071D49" /> Key Insights
                </span>
                <div className="flex flex-col gap-2">
                  {singleReport.keyInsights.map((insight, index) => (
                    <div key={index} className="flex flex-row justify-between">
                      <span className="flex flex-row items-center gap-1">
                        <DotOutline />
                        {insight}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              id="implementation-notes"
              className="flex w-full flex-col gap-4 border-b p-2 text-sm"
            >
              <span className="flex flex-row items-center gap-2 text-xl font-bold text-primary-700">
                <Pencil color="#071D49" /> Implementation Notes
              </span>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-gray-700">
                    Performance Thresholds
                  </span>
                  <div className="flex flex-row gap-6">
                    <div className="flex w-1/4 flex-col">
                      <span>FCP</span>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="green" />
                          Good:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.fcp.good}
                      </div>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="red" />
                          Poor:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.fcp.poor}
                      </div>
                    </div>
                    <div className="flex w-1/4 flex-col">
                      <span>LCP</span>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="green" />
                          Good:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.lcp.good}
                      </div>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="red" />
                          Poor:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.lcp.poor}
                      </div>
                    </div>
                    <div className="flex w-1/4 flex-col">
                      <span>CLS</span>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="green" />
                          Good:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.cls.good}
                      </div>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="red" />
                          Poor:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.cls.poor}
                      </div>
                    </div>
                    <div className="flex w-1/4 flex-col">
                      <span>TTFB</span>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="green" />
                          Good:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.ttfb.good}
                      </div>
                      <div className="flex w-full flex-row">
                        <span className="flex flex-row items-center gap-1">
                          <Circle weight="fill" color="red" />
                          Poor:
                        </span>{' '}
                        {singleReport.implementationNotes.thresholds.ttfb.poor}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-1">
                  <span className="font-semibold text-gray-700">
                    Best Practices
                  </span>
                  <div>
                    {singleReport.implementationNotes.bestPractices.map(
                      (practice, index) => (
                        <span
                          key={index}
                          className="flex flex-row items-center gap-1"
                        >
                          <span className="text-green-600">
                            <Check />
                          </span>
                          {practice}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {!singleReport && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={150} weight="bold" />
            <label className="text-xs font-medium tracking-wider text-neutral-500">
              Select a report
            </label>
          </span>
        )}
      </div>
    </div>
  );
};

export default ArtilleryMainPanel;
